import { TabItem } from 'components/common/BTabs';
import PriceWidgetExtraSection from 'components/Vehicle/VehicleDetail/PriceWidget/PriceWidgetExtraSection';
import { FormAction } from 'hooks/useForm';
import useTranslation from 'next-translate/useTranslation';
import { Dispatch } from 'react';
import {
  VehicleBasedDailyWeeklyDemandState,
  VehicleBasedDemandState,
} from 'types/demand.type';
import { Extra, ExtraWithUnit } from 'types/vehicle.type';

interface ExtraSections<
  T extends VehicleBasedDemandState | VehicleBasedDailyWeeklyDemandState
> {
  stateBaseKey: T extends VehicleBasedDemandState
    ? 'selectedMeal' | 'selectedServices' | 'selectedPackages'
    : 'selectedServices';
  stateUnitKey: keyof T;
  title: string;
  description?: string;
  showDescriptionWhen?: boolean;
  disabled?: boolean;
  list: ExtraWithUnit[];
  isValueSingular: boolean;
}

export function usePriceWidgetExtraSections<
  T extends VehicleBasedDemandState | VehicleBasedDailyWeeklyDemandState
>(
  state: T,
  dispatch: Dispatch<FormAction<T>>,
  supportedMeals: Extra[],
  supportedPackages: ExtraWithUnit[],
  supportedServices: ExtraWithUnit[],
  diningCapacity: number
) {
  const { t } = useTranslation('vehicle');
  const extrasSections: ExtraSections<T>[] = [];

  supportedMeals.length &&
    extrasSections.push({
      stateBaseKey: 'selectedMeal' as any,
      stateUnitKey: 'selectedMealsUnits' as any,
      title: 'mealsTitle',
      description:
        +state.numberOfPeople > diningCapacity
          ? 'mealsDescriptionNumberOfPeople'
          : 'mealsDescription',
      disabled: +state.numberOfPeople > diningCapacity,
      list: supportedMeals as ExtraWithUnit[],
      isValueSingular: true,
    });

  supportedPackages.length &&
    extrasSections.push({
      stateBaseKey: 'selectedPackage' as any,
      stateUnitKey: 'selectedPackagesUnits' as any,
      title: 'packagesTitle',
      description: 'packagesDescription',
      showDescriptionWhen: true,
      list: supportedPackages,
      isValueSingular: true,
    });
  supportedServices.length &&
    extrasSections.push({
      stateBaseKey: 'selectedServices',
      stateUnitKey: 'selectedServicesUnits',
      title: 'servicesTitle',
      list: supportedServices,
      isValueSingular: false,
    });

  const getExtraTabsChildren = (section: ExtraSections<T>) => {
    return (
      <PriceWidgetExtraSection
        key={section.stateBaseKey}
        getChecked={(val) =>
          section.isValueSingular
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error gunluk haftalik typelariyla cok cakisiyor
              state[section.stateBaseKey as keyof T] === val
            : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error gunluk haftalik typelariyla cok cakisiyor
              (state[section.stateBaseKey as keyof T] as number[]).includes(val)
        }
        list={section.list}
        description={
          section.description &&
          t(`priceWidget.form.extrasModal.${section.description}`)
        }
        disabled={section.disabled}
        onChange={(isChecked, value) => {
          if (section.isValueSingular) {
            dispatch({
              type: section.stateBaseKey as keyof T,
              payload: (isChecked ? value : null) as any,
            });
          } else {
            dispatch({
              type: section.stateBaseKey as keyof T,
              payload: value as any,
              isAppend: isChecked,
            });
          }
        }}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error gunluk haftalik typelariyla cok cakisiyor
        getUnits={(id) => (state[section.stateUnitKey] as T[keyof T])[id] || ''}
        onUnitChange={(id, value) => {
          dispatch({
            type: section.stateUnitKey,
            payload: {
              ...(state[section.stateUnitKey as keyof T] as T[keyof T]),
              [id]: value,
            } as unknown as T[keyof T],
          });
        }}
      />
    );
  };

  const extraTabs = extrasSections.reduce(
    (acc, section) => [
      ...acc,
      {
        title: t(`priceWidget.form.extrasModal.${section.title}`),
        children: getExtraTabsChildren(section),
      },
    ],
    [] as TabItem[]
  );

  return {
    extrasSections,
    getExtraTabsChildren,
    extraTabs,
  };
}
